import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import style from "./newOrderFilter.module.scss";
import CustomSelect from "../../custom/custom-select";
import CustomSelectMulti from "../../custom/custom-select/CustomSelectMulti";
import CustomButton from "../../custom/custom-button/CustomButton";
import FilterDatePicker from "../../custom/сustom-filter-date/FilterDatePicker";
import CustomInput from "../../custom/custom-input/CustomInput";
import { XIcon } from "lucide-react";
import { ORDER_STATUSES_AIR } from "../../../mock";
import FilterIcon from "../../shared/icons/FilterIcon";
import { Employee, Order } from "../../../services";
import { useLanguage } from "../../../context/LanguageContext";

const EMPLOYEE_FILTER_STATES = new Set(["Confirmed", "UnActive"]);

const AviaOrderFilter = ({
  isOpen,
  onClose,
  filterValues,
  filterSetters,
  filterMeta,
  statusActive,
  onApply,
  showEmployeeFilter = false,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const companyId = useSelector((state) => state.user?.companyId);

  const {
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    shippingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    orderNo,
    recipientName,
    selectedEmployee,
  } = filterValues;

  const {
    setCountryFrom,
    setCountryTo,
    setCityFrom,
    setCityTo,
    setShippingDate,
    setCreationDate,
    setSelectedStatus,
    setSelectedCurrency,
    setSenderName,
    setOrderNo,
    setRecipientName,
    setSelectedEmployee,
  } = filterSetters;

  const { countries } = filterMeta;

  const [localCountryFrom, setLocalCountryFrom] = useState(countryFrom || "");
  const [localCountryTo, setLocalCountryTo] = useState(countryTo || "");
  const [localCitiesFrom, setLocalCitiesFrom] = useState([]);
  const [localCitiesTo, setLocalCitiesTo] = useState([]);

  const [localCityFrom, setLocalCityFrom] = useState(cityFrom || "");
  const [localCityTo, setLocalCityTo] = useState(cityTo || "");
  const [localStatus, setLocalStatus] = useState(Array.isArray(selectedStatus) ? selectedStatus : []);
  const [localCurrency, setLocalCurrency] = useState(selectedCurrency || "");
  const [localSenderName, setLocalSenderName] = useState(senderName || "");
  const [localOrderNo, setLocalOrderNo] = useState(orderNo || "");
  const [localRecipientName, setLocalRecipientName] = useState(recipientName || "");
  const [shippingDates, setShippingDates] = useState(shippingDate || []);
  const [creationDates, setCreationDates] = useState(creationDate || []);
  const [localSelectedEmployee, setLocalSelectedEmployee] = useState(selectedEmployee || "");
  const [employees, setEmployees] = useState([]);
  const [selectKey, setSelectKey] = useState(0);

  useEffect(() => {
    if (!showEmployeeFilter || !companyId) {
      setEmployees([]);
      return;
    }

    let isActive = true;

    Employee.getByEmployees(companyId, 1, 100)
      .then((response) => {
        const employeeOptions = (response.data?.rows || [])
          .filter((employee) => EMPLOYEE_FILTER_STATES.has(employee.state))
          .map((employee) => ({
            ...employee,
            name: [employee.surname, employee.name, employee.patronymic]
              .map((namePart) => namePart?.trim())
              .filter(Boolean)
              .join(" "),
          }));

        if (isActive) {
          setEmployees(employeeOptions);
        }
      })
      .catch((error) => {
        console.error("Error getting employees:", error);
      });

    return () => {
      isActive = false;
    };
  }, [companyId, showEmployeeFilter]);

  useEffect(() => {
    if (!localCountryFrom) {
      setLocalCitiesFrom([]);
      setLocalCityFrom("");
      return;
    }
    Order.getCities(currentLanguage, localCountryFrom.id)
      .then((res) => res.data)
      .then((data) => {
        const sorted = data?.sort((a, b) => a.name.localeCompare(b.name));
        setLocalCitiesFrom(sorted || []);
      })
      .catch((error) => console.error("Error getting cities (from):", error));
  }, [localCountryFrom, currentLanguage]);

  useEffect(() => {
    if (!localCountryTo) {
      setLocalCitiesTo([]);
      setLocalCityTo("");
      return;
    }
    Order.getCities(currentLanguage, localCountryTo.id)
      .then((res) => res.data)
      .then((data) => {
        const sorted = data?.sort((a, b) => a.name.localeCompare(b.name));
        setLocalCitiesTo(sorted || []);
      })
      .catch((error) => console.error("Error getting cities (to):", error));
  }, [localCountryTo, currentLanguage]);

  useEffect(() => {
    if (isOpen) {
      setLocalCountryFrom(countryFrom || "");
      setLocalCountryTo(countryTo || "");
      setLocalCityFrom(cityFrom || "");
      setLocalCityTo(cityTo || "");
      setLocalStatus(Array.isArray(selectedStatus) ? selectedStatus : []);
      setLocalCurrency(selectedCurrency || "");
      setLocalSenderName(senderName || "");
      setLocalOrderNo(orderNo || "");
      setLocalRecipientName(recipientName || "");
      setShippingDates(shippingDate || []);
      setCreationDates(creationDate || []);
      setLocalSelectedEmployee(selectedEmployee || "");
    }
  }, [isOpen]);

  const statusOptions = Object.entries(ORDER_STATUSES_AIR(t)).map(([key, value]) => ({
    id: key,
    value: key,
    name: value,
  }));

  const currencyOptions = [
    { id: "USD", value: "USD", name: "USD" },
    { id: "KZT", value: "KZT", name: "KZT" },
    { id: "EUR", value: "EUR", name: "EUR" },
  ];

  const isStatusDisabled = statusActive && statusActive.key !== "all";

  const handleApply = () => {
    setCountryFrom(localCountryFrom);
    setCountryTo(localCountryTo);
    setCityFrom(localCityFrom);
    setCityTo(localCityTo);
    setShippingDate(shippingDates);
    setCreationDate(creationDates);
    setSelectedStatus(localStatus);
    setSelectedCurrency(localCurrency);
    setSenderName(localSenderName);
    setOrderNo(localOrderNo);
    setRecipientName(localRecipientName);
    setSelectedEmployee?.(localSelectedEmployee);
    onClose();
    if (onApply) {
      onApply({
        countryFrom: localCountryFrom,
        countryTo: localCountryTo,
        cityFrom: localCityFrom,
        cityTo: localCityTo,
        shippingDate: shippingDates,
        creationDate: creationDates,
        selectedStatus: localStatus,
        selectedCurrency: localCurrency,
        senderName: localSenderName,
        orderNo: localOrderNo,
        recipientName: localRecipientName,
        selectedEmployee: localSelectedEmployee,
      });
    }
  };

  const handleReset = () => {
    setLocalCountryFrom("");
    setLocalCountryTo("");
    setLocalCitiesFrom([]);
    setLocalCitiesTo([]);
    setLocalCityFrom("");
    setLocalCityTo("");
    setLocalStatus([]);
    setSelectKey((prev) => prev + 1);
    setLocalCurrency("");
    setLocalSenderName("");
    setLocalOrderNo("");
    setLocalRecipientName("");
    setLocalSelectedEmployee("");
    setShippingDates([]);
    setCreationDates([]);

    setCountryFrom("");
    setCountryTo("");
    setCityFrom("");
    setCityTo("");
    setShippingDate([]);
    setCreationDate([]);
    setSelectedStatus([]);
    setSelectedCurrency("");
    setSenderName("");
    setOrderNo("");
    setRecipientName("");
    setSelectedEmployee?.("");

    onClose();
    if (onApply) {
      onApply({
        countryFrom: "",
        countryTo: "",
        cityFrom: "",
        cityTo: "",
        shippingDate: [],
        creationDate: [],
        selectedStatus: [],
        selectedCurrency: "",
        senderName: "",
        orderNo: "",
        recipientName: "",
        selectedEmployee: "",
      });
    }
  };

  return (
    <div className={cn(style.overlay, { [style.overlay_open]: isOpen })}>
      <div className={style.backdrop} onClick={onClose} />

      <div className={style.drawer}>
        {/* Header */}
        <div className={style.header}>
          <div className={style.headerLeft}>
            <FilterIcon size={20} color="#212121" />
            <span className={style.title}>{t("orderFilter.title")}</span>
          </div>
          <button type="button" className={style.closeBtn} onClick={onClose}>
            <XIcon size={20} />
          </button>
        </div>

        {/* Body */}
        <div className={style.body}>

          {/* Место */}
          <div>
            <div className={style.sectionTitle}>{t("orderFilter.placeSection")}</div>
            <div className={style.sectionBody}>
              {/* From: страна + город */}
              <div className={style.row}>
                <div className={style.field}>
                  <CustomSelect
                    key={"countries-from"}
                    labelText={t("orderFilter.from")}
                    placeholder={t("orderFilter.countryFromLabel")}
                    options={countries}
                    defaultValue={localCountryFrom}
                    onSubmit={(item) => {
                      setLocalCountryFrom(item);
                      setLocalCityFrom("");
                    }}
                    returnObject
                    usePortal
                  />
                </div>
                <div className={style.field}>
                  <CustomSelect
                    key={"cities-from"}
                    labelText={t("orderFilter.from")}
                    placeholder={t("orderFilter.loadingCityPlaceholder")}
                    options={localCitiesFrom}
                    defaultValue={localCityFrom}
                    isDisabled={!localCountryFrom}
                    onSubmit={setLocalCityFrom}
                    returnObject
                    usePortal
                  />
                </div>
              </div>

              {/* To: страна + город */}
              <div className={style.row}>
                <div className={style.field}>
                  <CustomSelect
                    key={"countries-to"}
                    labelText={t("orderFilter.to")}
                    placeholder={t("orderFilter.countryToLabel")}
                    options={countries}
                    defaultValue={localCountryTo}
                    onSubmit={(item) => {
                      setLocalCountryTo(item);
                      setLocalCityTo("");
                    }}
                    returnObject
                    usePortal
                  />
                </div>
                <div className={style.field}>
                  <CustomSelect
                    key={"cities-to"}
                    labelText={t("orderFilter.to")}
                    placeholder={t("orderFilter.unloadingCityPlaceholder")}
                    options={localCitiesTo}
                    defaultValue={localCityTo}
                    isDisabled={!localCountryTo}
                    onSubmit={setLocalCityTo}
                    returnObject
                    usePortal
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Дата */}
          <div>
            <div className={style.sectionTitle}>{t("orderFilter.dateSection")}</div>
            <div className={style.sectionBody}>
              <div className={style.row}>
                <div className={style.field}>
                  <FilterDatePicker
                    isRange
                    isEditable
                    noMarginBottom
                    isMobile
                    labelText={t("aviaOrderFilter.startShippingDateLabel")}
                    placeholderText={t("orderFilter.datePlaceholder")}
                    date={shippingDates}
                    setDate={setShippingDates}
                  />
                </div>
                <div className={style.field}>
                  <FilterDatePicker
                    isRange
                    isEditable
                    noMarginBottom
                    isMobile
                    labelText={t("orderFilter.creationDate")}
                    placeholderText={t("orderFilter.datePlaceholder")}
                    date={creationDates}
                    setDate={setCreationDates}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Параметры заказа */}
          <div>
            <div className={style.sectionTitle}>{t("orderFilter.orderParamsSection")}</div>
            <div className={style.sectionBody}>
              <div className={style.row}>
                <div className={style.field}>
                  <CustomInput
                    labelText={t("orderFilter.senderName")}
                    placeholder={t("orderFilter.senderName")}
                    value={localSenderName}
                    onChangeValue={setLocalSenderName}
                    isBgGray
                  />
                </div>
                <div className={style.field}>
                  <CustomInput
                    labelText={t("orderFilter.receiverName")}
                    placeholder={t("orderFilter.receiverName")}
                    value={localRecipientName}
                    onChangeValue={setLocalRecipientName}
                    isBgGray
                  />
                </div>
              </div>
              <div className={style.row}>
                <div className={style.field}>
                  <CustomInput
                    inputType="text"
                    labelText={t("orderFilter.orderNo")}
                    placeholder={t("orderFilter.orderNo")}
                    value={localOrderNo}
                    onChangeValue={setLocalOrderNo}
                    isBgGray
                  />
                </div>
              </div>
              <div className={style.row}>
                <div className={style.field}>
                  <CustomSelectMulti
                    key={selectKey}
                    labelText={t("orderFilter.statusLabel")}
                    placeholder={t("orderFilter.statusPlaceholder")}
                    options={statusOptions}
                    defaultValue={isStatusDisabled ? [] : localStatus}
                    isMulti
                    onSubmit={setLocalStatus}
                    usePortal
                    forceMenuTop
                  />
                </div>
              </div>
              <div className={style.row}>
                <div className={style.field}>
                  <CustomSelect
                    usePortal
                    labelText={t("orderFilter.currency")}
                    placeholder={t("orderFilter.currencyPlaceholder")}
                    options={currencyOptions}
                    defaultValue={localCurrency}
                    returnObject
                    onSubmit={setLocalCurrency}
                  />
                </div>
                {showEmployeeFilter && (
                  <div className={style.field}>
                    <CustomSelect
                      labelText={t("orderFilter.employee")}
                      placeholder={t("orderFilter.employeePlaceholder")}
                      options={employees}
                      defaultValue={localSelectedEmployee}
                      returnObject
                      onSubmit={setLocalSelectedEmployee}
                      usePortal
                    />
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={style.footer}>
          <CustomButton text={t("orderFilter.resetButton")} isUnderline onClick={handleReset} />
          <button type="button" className={style.applyBtn} onClick={handleApply}>
            {t("orderFilter.applyButton")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AviaOrderFilter;
